import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useNotificationBadge } from '../../hooks/useNotificationBadge.js';
import { useUnreadDmCount } from '../../hooks/useUnreadDmCount.js';
import { useFcmToken } from '../../hooks/useFcmToken.js';
import type { User } from '@ridehub/shared';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { Button } from '../../components/Button.js';
import { UserRankBadge } from '../../components/UserRankBadge.js';
import { CHECKIN_RANK_LABELS, SPOT_MAKER_RANK_LABELS } from '@ridehub/shared';

const BADGE_TYPE_LABELS: Record<string, string> = {
  checkin: 'チェックインランク',
  spotMaker: 'スポットメイカーランク',
};

function BadgeWithPopup({
  rank,
  type,
  label,
}: {
  rank: string;
  type: 'checkin' | 'spotMaker';
  label: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex-shrink-0 cursor-pointer" onClick={() => setOpen(true)}>
        <UserRankBadge rank={rank} type={type} size="xl" showLabel={false} />
      </div>
      {open && (
        <div
          className="fixed inset-0 z-[3000] flex flex-col items-center justify-center bg-black/60"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-semibold text-gray-700">{BADGE_TYPE_LABELS[type]}</p>
            <div
              style={{
                width: 120,
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ transform: 'scale(2.5)', transformOrigin: 'center' }}>
                <UserRankBadge rank={rank} type={type} size="xl" showLabel={false} />
              </div>
            </div>
            <p className="text-gray-800 font-bold text-lg">{label}</p>
          </div>
        </div>
      )}
    </>
  );
}
import { AwardBadge } from '../../components/AwardBadge.js';
import { ProfileEditForm } from './ProfileEditForm.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';

interface UserAward {
  id: string;
  awardType: 'monthly' | 'annual';
  category: 'checkin' | 'spot';
  rank: number;
  year: number;
  month: number | null;
  score: number;
  createdAt: string;
}

interface UserStats {
  checkinCount: number;
  spotCount: number;
  recentCheckins: Array<{
    id: string;
    checkinAt: string;
    spot: { id: string; name: string; category: string };
  }>;
}

interface UserWithStats extends User, UserStats {
  spotMakerRank: string;
  checkinRank: string;
  followersCount: number;
  followingCount: number;
  isFollowing: boolean;
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.73 21a2 2 0 0 1-3.46 0"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400 flex-shrink-0"
    >
      <path
        d="M9 18L15 12L9 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-100">
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-bold text-gray-800 px-1 mb-2">{children}</h2>;
}

export function UserProfileView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, dbUser, logout, loading: authLoading } = useAuth();
  const { unreadCount } = useNotificationBadge();
  const { unreadCount: unreadDmCount } = useUnreadDmCount();
  const { permission: notifPermission, requestAndRegister } = useFcmToken();

  const targetId = id ?? dbUser?.id;
  const isOwnProfile = !id || id === dbUser?.id;

  const [profileData, setProfileData] = useState<UserWithStats | null>(null);
  const [awards, setAwards] = useState<UserAward[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<'copied' | null>(null);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useEffect(() => {
    // 認証状態がまだ確定していない場合は待機（自分のプロフィールの race condition 防止）
    if (authLoading) return;
    if (!targetId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      apiClient.get<UserWithStats>(`/users/${targetId}`),
      apiClient.get<UserAward[]>(`/users/${targetId}/awards`),
    ])
      .then(([userData, awardsData]) => {
        if (userData) {
          setProfileData(userData);
          setIsFollowing(userData.isFollowing ?? false);
          setFollowersCount(userData.followersCount ?? 0);
        }
        if (awardsData) setAwards(awardsData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [targetId, authLoading]);

  const handleShare = async () => {
    const url = `${window.location.origin}/landing`;
    const shareData = {
      title: 'RideHub',
      text: 'サイクリスト向けスポットガイドアプリ。近くのショップ・カフェ・休憩スポットを見つけよう！',
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // キャンセル（AbortError）は無視
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error(err);
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
      setShareFeedback('copied');
      setTimeout(() => setShareFeedback(null), 2000);
    }
  };

  const handleFollowToggle = async () => {
    if (!targetId) return;
    const prev = isFollowing;
    const prevCount = followersCount;
    setIsFollowing(!prev);
    setFollowersCount(prev ? prevCount - 1 : prevCount + 1);
    try {
      if (prev) {
        await apiClient.delete(`/follows/${targetId}`, { requireAuth: true });
      } else {
        await apiClient.post(`/follows/${targetId}`, {}, { requireAuth: true });
      }
    } catch {
      setIsFollowing(prev);
      setFollowersCount(prevCount);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await apiClient.delete('/users/me', { requireAuth: true });
      await logout();
    } catch {
      setDeleteError('削除に失敗しました');
      setDeleteAccountConfirm(false);
    }
  };

  if (!firebaseUser && !id) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-gray-500">ログインしていません</p>
        <Button onClick={() => (window.location.href = '/login')}>ログイン</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!profileData) {
    return <p className="text-center py-8 text-gray-500">ユーザーが見つかりません</p>;
  }

  return (
    <div className="p-4 max-w-lg mx-auto space-y-6 pb-8">
      {/* プロフィール */}
      <div>
        <div className="flex items-center justify-between px-1 mb-2">
          <h2 className="text-sm font-bold text-gray-800">プロフィール</h2>
          {isOwnProfile && typeof Notification !== 'undefined' && (
            <button
              onClick={() => {
                if (notifPermission === 'granted') {
                  navigate('/notifications');
                } else if (notifPermission === 'default') {
                  void requestAndRegister();
                }
              }}
              disabled={notifPermission === 'denied'}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="通知"
              title={
                notifPermission === 'denied'
                  ? 'ブラウザの設定から通知を許可してください'
                  : undefined
              }
            >
              <div className="relative">
                <BellIcon />
                {notifPermission === 'granted' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5 leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {notifPermission === 'default' && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full" />
                )}
              </div>
            </button>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-100 px-4 py-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
              {profileData.avatarUrl && (
                <img src={profileData.avatarUrl} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-800">{profileData.nickname}</p>
              {profileData.bio && <p className="text-sm text-gray-500 mt-0.5">{profileData.bio}</p>}
              <p className="text-xs text-gray-400 mt-0.5">
                {new Date(profileData.createdAt).toLocaleDateString('ja-JP')} 登録
              </p>
              <div className="flex gap-4 mt-1">
                <Link
                  to={`/users/${targetId}/followers`}
                  className="flex flex-col items-center text-gray-500 hover:text-primary transition-colors"
                >
                  <span className="font-bold text-sm text-gray-800 leading-tight">
                    {followersCount}
                  </span>
                  <span className="text-xs leading-tight">フォロワー</span>
                </Link>
                <Link
                  to={`/users/${targetId}/following`}
                  className="flex flex-col items-center text-gray-500 hover:text-primary transition-colors"
                >
                  <span className="font-bold text-sm text-gray-800 leading-tight">
                    {profileData.followingCount ?? 0}
                  </span>
                  <span className="text-xs leading-tight">フォロー中</span>
                </Link>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <div className="flex flex-row items-center gap-3">
                <BadgeWithPopup
                  rank={profileData.checkinRank}
                  type="checkin"
                  label={CHECKIN_RANK_LABELS[profileData.checkinRank] ?? '−'}
                />
                <BadgeWithPopup
                  rank={profileData.spotMakerRank}
                  type="spotMaker"
                  label={SPOT_MAKER_RANK_LABELS[profileData.spotMakerRank] ?? '−'}
                />
              </div>
              {!isOwnProfile && dbUser && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void handleFollowToggle()}
                    className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                      isFollowing
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {isFollowing ? 'フォロー中' : 'フォロー'}
                  </button>
                  <button
                    onClick={() => navigate(`/messages/new?to=${profileData.id}`)}
                    className="text-xs font-medium px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    DM
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ギア紹介 */}
      {(profileData.bikePhotoUrl || profileData.gearInfo) && (
        <div>
          <SectionLabel>ギア紹介</SectionLabel>
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
            {profileData.bikePhotoUrl && (
              <img
                src={profileData.bikePhotoUrl}
                alt="愛車"
                className="w-full max-h-52 object-cover"
              />
            )}
            {profileData.gearInfo && (
              <div className="divide-y divide-gray-100">
                {(
                  [
                    ['フレーム/モデル', profileData.gearInfo.frameModel],
                    ['コンポーネント', profileData.gearInfo.component],
                    ['ホイール', profileData.gearInfo.wheel],
                    ['タイヤ', profileData.gearInfo.tire],
                    ['ペダル・シューズ', profileData.gearInfo.pedalShoes],
                    ['サイクルコンピューター', profileData.gearInfo.cycleComputer],
                    ['フロント/リアライト', profileData.gearInfo.lights],
                    ['ヘルメット/アイウェア', profileData.gearInfo.helmetEyewear],
                    ['お気に入りウェアブランド', profileData.gearInfo.favoriteWearBrand],
                    ['スマートウォッチ', profileData.gearInfo.smartWatch],
                    ['その他', profileData.gearInfo.other],
                  ] as [string, string | undefined][]
                )
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label} className="flex justify-between items-start gap-2 px-4 py-2.5">
                      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
                      <span className="text-xs text-gray-800 text-right">{value}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ライドスタイル（活動エリア + 走行スタイル） */}
      {((profileData.activityAreas?.length ?? 0) > 0 ||
        (profileData.ridingStyle &&
          ((profileData.ridingStyle.styles?.length ?? 0) > 0 ||
            profileData.ridingStyle.monthlyDistance))) && (
        <div>
          <SectionLabel>ライドスタイル</SectionLabel>
          <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-100">
            {(profileData.activityAreas?.length ?? 0) > 0 && (
              <div className="flex justify-between items-start gap-2 px-4 py-2.5">
                <span className="text-xs text-gray-500 flex-shrink-0 pt-0.5">活動エリア</span>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {profileData.activityAreas!.map((area) => (
                    <span
                      key={area}
                      className="inline-block border border-gray-200 text-gray-400 text-xs px-2.5 py-1 rounded-full"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(profileData.ridingStyle?.styles?.length ?? 0) > 0 && (
              <div className="flex justify-between items-start gap-2 px-4 py-2.5">
                <span className="text-xs text-gray-500 flex-shrink-0 pt-0.5">走行スタイル</span>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {profileData.ridingStyle!.styles!.map((s) => (
                    <span
                      key={s}
                      className="inline-block border border-gray-200 text-gray-400 text-xs px-2.5 py-1 rounded-full"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {profileData.ridingStyle?.monthlyDistance && (
              <div className="flex justify-between items-center gap-2 px-4 py-2.5">
                <span className="text-xs text-gray-500">月間走行距離</span>
                <span className="text-xs text-gray-800">
                  {profileData.ridingStyle.monthlyDistance}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* アワードギャラリー */}
      {awards.length > 0 && (
        <div>
          <SectionLabel>アワードギャラリー</SectionLabel>
          <div className="bg-white rounded-lg border border-gray-100 px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {awards.map((award) => (
                <AwardBadge
                  key={award.id}
                  awardType={award.awardType}
                  category={award.category}
                  rank={award.rank}
                  year={award.year}
                  month={award.month}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* アクティビティ */}
      <div>
        <SectionLabel>アクティビティ</SectionLabel>
        <SectionCard>
          <Link
            to={`/users/${targetId}/checkins`}
            className="flex justify-between items-center px-4 py-3 text-sm"
          >
            <span className="text-gray-700">チェックイン</span>
            <div className="flex items-center gap-2">
              <span className="text-primary font-bold">{profileData.checkinCount}</span>
              <ChevronRight />
            </div>
          </Link>
          <Link
            to={`/users/${targetId}/spots`}
            className="flex justify-between items-center px-4 py-3 text-sm"
          >
            <span className="text-gray-700">登録スポット</span>
            <div className="flex items-center gap-2">
              <span className="text-primary font-bold">{profileData.spotCount}</span>
              <ChevronRight />
            </div>
          </Link>
          {isOwnProfile && (
            <Link to="/bookmarks" className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-700">ブックマーク</span>
              <ChevronRight />
            </Link>
          )}
          {isOwnProfile && (
            <Link to="/messages" className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-700">ダイレクトメッセージ</span>
              <div className="flex items-center gap-2">
                {unreadDmCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none">
                    {unreadDmCount > 99 ? '99+' : unreadDmCount}
                  </span>
                )}
                <ChevronRight />
              </div>
            </Link>
          )}
        </SectionCard>
      </div>

      {/* アカウント（自分のみ・非編集時） */}
      {isOwnProfile && !editing && (
        <div>
          <SectionLabel>アカウント</SectionLabel>
          <SectionCard>
            <button
              onClick={() => setEditing(true)}
              className="w-full flex justify-between items-center px-4 py-3 text-sm"
            >
              <span className="text-gray-700">プロフィール編集</span>
              <ChevronRight />
            </button>
            <Link
              to="/notification-settings"
              className="w-full flex justify-between items-center px-4 py-3 text-sm"
            >
              <span className="text-gray-700">通知設定</span>
              <ChevronRight />
            </Link>
            <button
              onClick={() => void logout()}
              className="w-full flex justify-between items-center px-4 py-3 text-sm"
            >
              <span className="text-gray-700">ログアウト</span>
              <ChevronRight />
            </button>
          </SectionCard>
        </div>
      )}

      {/* プロフィール編集フォーム */}
      {isOwnProfile && editing && (
        <div>
          <SectionLabel>プロフィール編集</SectionLabel>
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <ProfileEditForm
              user={profileData}
              onSave={(updated) => {
                setProfileData({ ...profileData, ...updated });
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}

      {/* ヘルプ */}
      <div>
        <SectionLabel>ヘルプ</SectionLabel>
        <SectionCard>
          <Link to="/help" className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">使い方ガイド</span>
            <ChevronRight />
          </Link>
          <Link to="/faq" className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">よくある質問</span>
            <ChevronRight />
          </Link>
          <a
            href="https://tally.so/r/kdWrRJ"
            target="_blank"
            rel="noopener noreferrer"
            className="flex justify-between items-center px-4 py-3 text-sm"
          >
            <span className="text-gray-700">フィードバックを送る</span>
            <ChevronRight />
          </a>
          <button
            onClick={() => void handleShare()}
            className="w-full flex justify-between items-center px-4 py-3 text-sm"
          >
            <span className="text-gray-700">友達に紹介する</span>
            <span className="text-xs text-primary font-medium">
              {shareFeedback === 'copied' ? 'URLをコピーしました' : '共有'}
            </span>
          </button>
        </SectionCard>
      </div>

      {/* アプリ情報 */}
      <div>
        <SectionLabel>アプリ情報</SectionLabel>
        <SectionCard>
          <div className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">アプリ名</span>
            <span className="text-gray-400">RideHub</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">開発者</span>
            <span className="text-gray-400">shiget</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">バージョン</span>
            <span className="text-gray-400">{__APP_VERSION__}</span>
          </div>
          <Link to="/release-notes" className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="text-gray-700">リリースノート</span>
            <ChevronRight />
          </Link>
        </SectionCard>
      </div>

      {/* 危険な操作（自分のみ・非編集時・ページ最下部） */}
      {isOwnProfile && !editing && (
        <div>
          <SectionLabel>Danger</SectionLabel>
          <div className="bg-white rounded-lg border border-red-200">
            <p className="text-xs text-red-400 px-4 pt-3 pb-1">
              以下の操作は取り消せません。実行前に十分ご確認ください。
            </p>
            <button
              onClick={() => setDeleteAccountConfirm(true)}
              className="w-full flex justify-between items-center px-4 py-3 text-sm border-t border-red-100"
            >
              <span className="text-red-500 font-medium">アカウントを削除する</span>
              <ChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* アカウント削除確認ダイアログ */}
      {deleteAccountConfirm && (
        <ConfirmDialog
          title="アカウントを削除"
          message={'アカウントを削除しますか？\nこの操作は取り消せません。'}
          onConfirm={() => void handleDeleteAccount()}
          onCancel={() => setDeleteAccountConfirm(false)}
        />
      )}

      {/* 操作エラーバナー */}
      {deleteError && (
        <div
          className="fixed left-4 right-4 z-[3000] bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex justify-between items-center shadow-md"
          style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px) + 8px)' }}
        >
          <p className="text-sm text-red-700">{deleteError}</p>
          <button
            onClick={() => setDeleteError(null)}
            className="text-red-400 ml-3 text-xl leading-none"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
